package co.maytes.standupbot.dashboard.components.widgets

import androidx.compose.runtime.Composable
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideAlertCircle
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideX
import co.maytes.standupbot.dashboard.styles.ControlClass
import co.maytes.standupbot.dashboard.styles.IconSize
import co.maytes.standupbot.dashboard.styles.Motion
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Space
import co.maytes.standupbot.shared.design.StatusHue
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.onMouseDown
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import org.jetbrains.compose.web.css.CSSSizeValue
import org.jetbrains.compose.web.css.CSSUnit
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.w3c.dom.HTMLElement
import org.jetbrains.compose.web.dom.Button as ButtonElement

internal class ElementHolder {
    var element: HTMLElement? = null
}

internal val AdornmentIconPx: Int = IconSize.sm.value.toInt()

internal fun fieldBoxLook(metrics: ControlMetrics, trailingSlots: Int = 0): Modifier =
    Modifier
        .classNames(ControlClass.FIELD)
        .height(metrics.height)
        .fontSize(metrics.fontSize)
        .styleModifier {
            property("padding-left", "${metrics.padX}")
            property("padding-right", trailingPadding(metrics.padX, trailingSlots))
            property("line-height", "1.4")
        }

fun Modifier.fieldControlLook(size: ControlSize = ControlSize.Md): Modifier = then(fieldBoxLook(size.metrics))

internal fun trailingPadding(padX: CSSSizeValue<CSSUnit.px>, slots: Int): String =
    if (slots <= 0) "$padX" else "calc($padX + $slots * (${IconSize.lg} + ${Space.s1}))"

@Composable
internal fun FieldAdornments(
    padX: CSSSizeValue<CSSUnit.px>,
    alignTop: Boolean = false,
    content: @Composable () -> Unit,
) {
    Div(
        attrs = Modifier
            .classNames(ControlClass.FIELD_ADORNMENT)
            .styleModifier {
                property("right", "$padX")
                if (alignTop) {
                    property("bottom", "auto")
                    property("align-items", "flex-start")
                    property("padding-top", "${Space.s2}")
                }
            }
            .toAttrs(),
    ) { content() }
}

@Composable
internal fun AdornmentSlot(content: @Composable () -> Unit) {
    Span(
        attrs = Modifier
            .width(IconSize.lg)
            .height(IconSize.lg)
            .styleModifier {
                property("display", "inline-flex")
                property("align-items", "center")
                property("justify-content", "center")
            }
            .toAttrs(),
    ) { content() }
}

@Composable
internal fun InvalidAdornment() {
    AdornmentSlot {
        LucideAlertCircle(size = AdornmentIconPx, strokeColor = Palette.status(StatusHue.Danger).solid.toString())
    }
}

@Composable
internal fun ClearAdornment(visible: Boolean, onClear: () -> Unit) {
    val base = Modifier
        .classNames(ControlClass.FIELD_CLEAR)
        .width(IconSize.lg)
        .height(IconSize.lg)
    ButtonElement(
        attrs = (if (visible) base.classNames(Motion.OPEN_CLASS) else base)
            .onMouseDown { event -> event.preventDefault() }
            .onClick { onClear() }
            .toAttrs {
                attr("type", "button")
                attr("tabindex", "-1")
                attr("aria-label", "Clear")
                if (!visible) attr("aria-hidden", "true")
            },
    ) {
        LucideX(size = AdornmentIconPx)
    }
}
